import React, { createContext, useContext, useEffect, useState } from 'react';
import { authApi } from '@/lib/api';

interface User {
  id: string;
  email: string;
  role: 'user' | 'admin';
  email_verified?: boolean;
  created_at?: string;
}

interface AuthContextType {
  user: User | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (email: string, password: string, turnstileToken?: string) => Promise<void>;
  signOut: () => Promise<void>;
  isAdmin: boolean;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    // Check if user is already logged in
    checkAuth();
  }, []);

  const checkAuth = async () => {
    if (!authApi.isAuthenticated()) {
      setLoading(false);
      return;
    }

    try {
      const result = await authApi.getMe();
      if (result.success && result.data) {
        const userData: User = {
          id: result.data.id,
          email: result.data.email,
          role: result.data.role,
          email_verified: !!(result.data as any).email_verified,
          created_at: result.data.created_at
        };
        setUser(userData);
        setIsAdmin(result.data.role === 'admin');
      } else {
        // Token invalid, clear it
        await authApi.logout();
      }
    } catch (error) {
      console.error('Auth check failed:', error);
    } finally {
      setLoading(false);
    }
  };

  const refreshUser = async () => {
    if (!authApi.isAuthenticated()) return;
    try {
      const result = await authApi.getMe();
      if (result.success && result.data) {
        const userData: User = {
          id: result.data.id,
          email: result.data.email,
          role: result.data.role,
          email_verified: !!(result.data as any).email_verified,
          created_at: result.data.created_at
        };
        setUser(userData);
        setIsAdmin(result.data.role === 'admin');
      }
    } catch (error) {
      console.error('Refresh user failed:', error);
    }
  };

  const signIn = async (email: string, password: string) => {
    const result = await authApi.login(email, password);
    
    if (!result.success) {
      throw new Error(result.error || 'Login failed');
    }

    if (result.data) {
      const userData: User = {
        id: result.data.user.id,
        email: result.data.user.email,
        role: result.data.user.role as 'user' | 'admin'
      };
      setUser(userData);
      setIsAdmin(userData.role === 'admin');
    }
  };

  const signUp = async (email: string, password: string, turnstileToken?: string) => {
    const result = await authApi.register(email, password, turnstileToken);
    
    if (!result.success) {
      throw new Error(result.error || 'Registration failed');
    }

    if (result.data) {
      const userData: User = {
        id: result.data.user.id,
        email: result.data.user.email,
        role: result.data.user.role as 'user' | 'admin'
      };
      setUser(userData);
      setIsAdmin(userData.role === 'admin');
    }
  };

  const signOut = async () => {
    await authApi.logout();
    setUser(null);
    setIsAdmin(false);
  };

  const value = {
    user,
    loading,
    signIn,
    signUp,
    signOut,
    isAdmin,
    refreshUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
